import { Box, BoxProps } from '@chakra-ui/react';
import { FC } from 'react';
import parse, { HTMLReactParserOptions } from 'html-react-parser';
import { replaceAnchor } from 'presentation/utils/replaceAnchor';

interface Props extends BoxProps {
  content: string;
}

const htmlContentStyles = {
  '&::after': {
    content: '""',
    display: 'table',
    clear: 'both',
  },
  '& img': {
    borderRadius: '12px',
    maxWidth: '100%',
    marginY: '15px',
  },
  '& iframe': {
    borderRadius: '12px',
    maxWidth: '100%',
    marginY: '15px',
  },
  '& p': {
    marginBottom: '12px',
    lineHeight: '1.6',
  },
  '& h1': {
    fontWeight: 700,
    fontSize: '2rem',
    marginTop: '20px',
    marginBottom: '10px',
  },
  '& h2': {
    fontWeight: 700,
    fontSize: '1.75rem',
    marginTop: '20px',
    marginBottom: '10px',
  },
  '& h3': {
    fontWeight: 700,
    fontSize: '1.5rem',
    marginTop: '20px',
    marginBottom: '10px',
  },
  '& h4': {
    fontWeight: 700,
    fontSize: '1.25rem',
    marginTop: '20px',
    marginBottom: '10px',
  },
  '& h5': {
    fontWeight: 700,
    fontSize: '1.125rem',
    marginTop: '20px',
    marginBottom: '10px',
  },
  '& h6': {
    fontWeight: 700,
    fontSize: '1rem',
    marginTop: '20px',
    marginBottom: '10px',
  },
  '& ul, & ol': {
    listStyle: 'none',
    paddingLeft: '0',
    marginBottom: '12px',
    counterReset: 'list-counter',
  },
  '& li': {
    marginBottom: '12px',
    position: 'relative',
    paddingLeft: '32px',
  },
  '& ol:not([data-list-style-type]) li': {
    counterIncrement: 'list-counter',
  },
  '& ol:not([data-list-style-type]) li::before': {
    content: 'counter(list-counter) "."',
    position: 'absolute',
    left: '0',
    top: '0',
    fontWeight: 600,
    color: 'primary.500',
    minWidth: '24px',
  },
  '& ol[data-list-style-type="lower-alpha"]': {
    counterReset: 'list-counter-alpha',
  },
  '& ol[data-list-style-type="lower-alpha"] li': {
    counterIncrement: 'list-counter-alpha',
  },
  '& ol[data-list-style-type="lower-alpha"] li::before': {
    content: 'counter(list-counter-alpha, lower-alpha) ")"',
    position: 'absolute',
    left: '0',
    top: '0',
    fontWeight: 600,
    color: 'primary.500',
    minWidth: '24px',
  },
  '& ol[data-list-style-type="upper-alpha"]': {
    counterReset: 'list-counter-upper-alpha',
  },
  '& ol[data-list-style-type="upper-alpha"] li': {
    counterIncrement: 'list-counter-upper-alpha',
  },
  '& ol[data-list-style-type="upper-alpha"] li::before': {
    content: 'counter(list-counter-upper-alpha, upper-alpha) ")"',
    position: 'absolute',
    left: '0',
    top: '0',
    fontWeight: 600,
    color: 'primary.500',
    minWidth: '24px',
  },
  '& ol[data-list-style-type="lower-roman"]': {
    counterReset: 'list-counter-lower-roman',
  },
  '& ol[data-list-style-type="lower-roman"] li': {
    counterIncrement: 'list-counter-lower-roman',
  },
  '& ol[data-list-style-type="lower-roman"] li::before': {
    content: 'counter(list-counter-lower-roman, lower-roman) ")"',
    position: 'absolute',
    left: '0',
    top: '0',
    fontWeight: 600,
    color: 'primary.500',
    minWidth: '24px',
  },
  '& ol[data-list-style-type="upper-roman"]': {
    counterReset: 'list-counter-upper-roman',
  },
  '& ol[data-list-style-type="upper-roman"] li': {
    counterIncrement: 'list-counter-upper-roman',
  },
  '& ol[data-list-style-type="upper-roman"] li::before': {
    content: 'counter(list-counter-upper-roman, upper-roman) ")"',
    position: 'absolute',
    left: '0',
    top: '0',
    fontWeight: 600,
    color: 'primary.500',
    minWidth: '24px',
  },
  '& ol[data-list-style-type="lower-greek"]': {
    counterReset: 'list-counter-lower-greek',
  },
  '& ol[data-list-style-type="lower-greek"] li': {
    counterIncrement: 'list-counter-lower-greek',
  },
  '& ol[data-list-style-type="lower-greek"] li::before': {
    content: 'counter(list-counter-lower-greek, lower-greek) ")"',
    position: 'absolute',
    left: '0',
    top: '0',
    fontWeight: 600,
    color: 'primary.500',
    minWidth: '24px',
  },
  '& ul:not([data-list-style]) li::before': {
    content: '""',
    position: 'absolute',
    left: '8px',
    top: '8px',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'primary.500',
    boxShadow: '0 0 0 6px rgba(52, 199, 130, 0.15)',
  },
  '& ul[data-list-style="square"] li::before': {
    content: '""',
    position: 'absolute',
    left: '8px',
    top: '8px',
    width: '10px',
    height: '10px',
    borderRadius: '0',
    backgroundColor: 'primary.500',
    boxShadow: '0 0 0 6px rgba(52, 199, 130, 0.15)',
  },
  '& ul[data-list-style="circle"] li::before': {
    content: '""',
    position: 'absolute',
    left: '8px',
    top: '8px',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    border: '2px solid',
    borderColor: 'primary.500',
    boxShadow: '0 0 0 6px rgba(52, 199, 130, 0.15)',
  },
  '& a': {
    color: 'primary.500',
    textDecoration: 'underline',
  },
  '& strong, & b': {
    fontWeight: 700,
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    marginY: '16px',
    fontSize: '14px',
  },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'gray.200',
    padding: '12px',
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: 'gray.50',
    fontWeight: 600,
  },
  '& tr:nth-of-type(even)': {
    backgroundColor: 'gray.50',
  },
};

const options: HTMLReactParserOptions = {
  replace: (node, index) => replaceAnchor(node, index),
};

const HtmlContentBox: FC<Props> = ({ content, sx, ...props }) => {
  return (
    <Box sx={{ ...htmlContentStyles, ...sx }} {...props}>
      {parse(content, options)}
    </Box>
  );
};

export default HtmlContentBox;
